import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../../firebase/config";

export type PosInformationItem = { id: string; label: string; details?: { id: string; label: string }[] };
export type PosInformationGroup = { id: string; label: string; items: PosInformationItem[] };

export const POS_INFORMATION_GROUPS: PosInformationGroup[] = [
  {
    id: "settings",
    label: "Settings",
    items: [
      { id: "tab-org", label: "Organization" },
      { id: "tab-0", label: "Store Details", details: [
        { id: "store-information", label: "Store Information" },
        { id: "workstation-setup", label: "Workstation registration" },
        { id: "workstation-sync", label: "Workstation sync" },
        { id: "store-logo", label: "Store Logo" },
        { id: "idle-timeout", label: "Screen Idle Time (minutes)" },
        { id: "location-control", label: "Location control" },
        { id: "store-hours", label: "Store Hours" },
        { id: "special-hours", label: "Special Hours" },
      ] },
      { id: "tab-1", label: "Store Operation", details: [
        { id: "store-type", label: "Store Type" },
        { id: "taxes-fees", label: "Taxes & Fees" },
        { id: "guest-groups", label: "Guest groups" },
        { id: "table-ordering", label: "Table Ordering Setting" },
        { id: "session-display", label: "Session & Display Settings" },
        { id: "keypad-popup-at-login", label: "Keypad popup at login" },
        { id: "dashboard-cards", label: "POS Dashboard Cards" },
        { id: "pos-theme", label: "POS Interface Theme" },
        { id: "customer-screen", label: "Customer screen" },
        { id: "table-view", label: "Table View" },
        { id: "reservations", label: "Reservations" },
        { id: "offline", label: "Offline" },
        { id: "eod-settings", label: "End of Day" },
        { id: "subscription", label: "Subscription" },
        { id: "training-mode", label: "Training Mode" },
        { id: "regional-integration", label: "Regional & Integration Settings" },
      ] },
      { id: "tab-4", label: "Payment", details: [
        { id: "payment-settings", label: "Payment Settings" },
      ] },
      { id: "tab-5", label: "Payment Processor" },
      { id: "tab-6", label: "Print", details: [
        { id: "receipt-delivery", label: "Print and email receipts" },
        { id: "cup-label", label: "Cup Label Template" },
        { id: "printers-list", label: "Virtual printer list" },
      ] },
      { id: "tab-7", label: "Webhooks", details: [
        { id: "webhooks", label: "Webhooks Integration" },
      ] },
      { id: "tab-8", label: "Users" },
      { id: "tab-20", label: "KDS User Settings" },
      { id: "tab-21", label: "Voice" },
      { id: "tab-9", label: "Data Management", details: [
        { id: "data-management", label: "Data Management" },
        { id: "export-data", label: "Export Data" },
      ] },
      { id: "tab-11", label: "Online ordering" },
      { id: "tab-23", label: "QR Table", details: [
        { id: "table-management", label: "Table Management" },
        { id: "qr-code-print", label: "QR Code Print Setting" },
        { id: "additional-qr", label: "Additional QR Setting" },
        { id: "qr-language", label: "Language Settings" },
      ] },
      { id: "tab-12", label: "Cloud Sync", details: [
        { id: "cloud-storage", label: "Cloud Storage Operations" },
      ] },
      { id: "tab-13", label: "OEM" },
      { id: "tab-14", label: "3PL" },
      { id: "tab-15", label: "Kiosk" },
      { id: "tab-16", label: "SD" },
      { id: "tab-17", label: "SR" },
      { id: "tab-18", label: "LN" },
      { id: "tab-19", label: "Uber" },
    ],
  },
  {
    id: "printers",
    label: "Printers",
    items: [
      { id: "printersPrinters", label: "Printers" },
      { id: "printersSettings", label: "MEV Settings" },
      { id: "printersLogs", label: "Logs" },
      { id: "printersDiscovery", label: "Discovery" },
    ],
  },
  {
    id: "cloudMev",
    label: "Cloud MEV",
    items: [
      { id: "cloudMevMonitor", label: "MEV Order Monitor" },
      { id: "cloudMevStatus", label: "Status" },
      { id: "cloudMevCertificate", label: "Certificate" },
      { id: "cloudMevUserReport", label: "User Report" },
      { id: "cloudMevLogs", label: "Logs" },
      { id: "cloudMevHowTo", label: "How to" },
    ],
  },
  {
    id: "menu",
    label: "Menu",
    items: [
      { id: "menus", label: "Menus" },
      { id: "options", label: "Advance Option" },
      { id: "tags", label: "Item Tags" },
      { id: "menu-setup", label: "Categories" },
      { id: "order-rules", label: "Order Rules" },
      { id: "menu-display", label: "Menu display" },
      { id: "recipes", label: "Recipe" },
    ],
  },
];

export function posInformationKey(groupId: string, itemId: string) {
  return `${groupId}:${itemId}`;
}

export function posInformationDetailKey(groupId: string, itemId: string, detailId: string) {
  return `${groupId}:${itemId}:${detailId}`;
}

export type NotePair = { en: string; fr: string };

export const VIRTUAL_PRINTER_LIST_NOTE: NotePair = {
  en: `[h]What is the Cloud MEV printer?[/h]
The Cloud MEV printer is a required virtual printer. It prints the customer receipt and the merchant receipt. The customer receipt includes the QR code and the Revenu Québec information. The customer verifies the receipt by scanning the QR code.

Some of its settings can be changed. It cannot be deleted.

[h]What is Kitchen All?[/h]
Kitchen All is the required virtual printer for the kitchen. Its dish list stays fully selected, so every kitchen dish prints on it. A dish added later is assigned to this virtual printer automatically.

Some settings can be changed, including the cut behavior. It cannot be renamed or deleted.

[h]What is a virtual printer? Why do I need it?[/h]
Split printing sends kitchen dishes to different printers.

A virtual printer is required. It groups the dishes that print together. Assign those dishes to the virtual printer, and set the cut behavior for that group.

[red]A dish that is not assigned to a virtual printer is not printed.[/red]

[h]I have three workstations: Vegetable, Meat, and Bar. Each workstation has its own printer. How do I set up split printing?[/h]
1. Create three virtual printers, one for each workstation, and assign the corresponding dishes to each virtual printer.
2. Go to Printers, create a printer, and assign the corresponding virtual printer to the physical printer.
3. When a new vegetable, meat, liquor, or wine item is added to the menu, assign it to the virtual printer so it is printed.`,
  fr: `[h]Qu'est-ce que l'imprimante Cloud MEV ?[/h]
L'imprimante Cloud MEV est une imprimante virtuelle obligatoire. Elle imprime le reçu du client et le reçu du commerçant. Le reçu du client contient le code QR et les renseignements de Revenu Québec. Le client vérifie le reçu en balayant le code QR.

Certains de ses paramètres peuvent être modifiés. Elle ne peut pas être supprimée.

[h]Qu'est-ce que Kitchen All ?[/h]
Kitchen All est l'imprimante virtuelle obligatoire pour la cuisine. Sa liste de plats reste entièrement sélectionnée, donc chaque plat de cuisine y est imprimé. Un plat ajouté ensuite est assigné automatiquement à cette imprimante virtuelle.

Certains paramètres peuvent être modifiés, y compris le comportement de coupe. Elle ne peut pas être renommée ni supprimée.

[h]Qu'est-ce qu'une imprimante virtuelle ? Pourquoi en ai-je besoin ?[/h]
L'impression répartie envoie les plats de cuisine vers des imprimantes différentes.

Une imprimante virtuelle est requise. Elle regroupe les plats qui s'impriment ensemble. Assignez ces plats à l'imprimante virtuelle et définissez le comportement de coupe de ce groupe.

[red]Un plat qui n'est assigné à aucune imprimante virtuelle n'est pas imprimé.[/red]

[h]J'ai trois postes : Légumes, Viande et Bar. Chaque poste a sa propre imprimante. Comment configurer l'impression répartie ?[/h]
1. Créez trois imprimantes virtuelles, une pour chaque poste, et assignez les plats correspondants à chaque imprimante virtuelle.
2. Allez dans Imprimantes, créez une imprimante, et assignez l'imprimante virtuelle correspondante à l'imprimante physique.
3. Lorsqu'un nouvel article de légumes, de viande, de spiritueux ou de vin est ajouté au menu, assignez-le à l'imprimante virtuelle pour qu'il soit imprimé.`,
};

export const WORKSTATION_REGISTRATION_NOTE: NotePair = {
  en: `[h]Workstation registration[/h]
Register this workstation in the Nuage POS Windows application. The same computer remains recognized after Windows or the application is reinstalled.

[red]This cannot be completed in the web browser.[/red]

[h]Before you submit[/h]
Open [b]Settings[/b], [b]Store details[/b], [b]Workstation registration[/b].

Choose the station type: [b]POS terminal[/b], [b]Tablet[/b], or [b]Kiosk[/b]. For a POS terminal, choose a station letter from A to Y. A letter already used by another workstation cannot be selected again.

Enter the [b]station name[/b]. The name can be changed later. Earlier names stay in the station name history.

The [b]station ID[/b] is assigned when this workstation is approved. It cannot be changed.

Enter the [b]workstation location[/b]. The technical contact is optional.

Agree to send this workstation's computer information to Nuage POS for approval, then select [b]Submit registration request[/b].

[h]After you submit[/h]
The registration status changes to [orange]Pending approval[/orange]. A super admin must approve this workstation before it can be used.

When the request is approved, the status becomes [green]Registered[/green] and the station ID appears.

If the request is [red]Rejected[/red], read the remarks, correct the details, and submit again.

If the workstation is [navy]Deactivated[/navy], a super admin can reactivate it.`,
  fr: `[h]Enregistrement du poste[/h]
Enregistrez ce poste dans l'application Windows Nuage POS. Le même ordinateur reste reconnu après la réinstallation de Windows ou de l'application.

[red]Cette opération ne peut pas être effectuée dans le navigateur Web.[/red]

[h]Avant l'envoi[/h]
Ouvrez [b]Paramètres[/b], [b]Détails du magasin[/b], [b]Enregistrement du poste[/b].

Choisissez le type de poste : [b]Terminal de point de vente[/b], [b]Tablette[/b] ou [b]Kiosque[/b]. Pour un terminal de point de vente, choisissez une lettre de poste de A à Y. Une lettre déjà utilisée par un autre poste ne peut pas être choisie de nouveau.

Entrez le [b]nom du poste[/b]. Le nom peut être modifié plus tard. Les noms précédents restent dans l'historique.

L'[b]identifiant du poste[/b] est attribué lorsque ce poste est approuvé. Il ne peut pas être modifié.

Entrez l'[b]emplacement du poste[/b]. Le contact technique est facultatif.

Acceptez d'envoyer les renseignements de l'ordinateur de ce poste à Nuage POS pour approbation, puis sélectionnez [b]Envoyer la demande d'enregistrement[/b].

[h]Après l'envoi[/h]
Le statut d'enregistrement passe à [orange]En attente d'approbation[/orange]. Un super administrateur doit approuver ce poste avant qu'il puisse être utilisé.

Lorsque la demande est approuvée, le statut devient [green]Enregistré[/green] et l'identifiant du poste apparaît.

Si la demande est [red]Refusée[/red], lisez les remarques, corrigez les détails et envoyez de nouveau.

Si le poste est [navy]Désactivé[/navy], un super administrateur peut le réactiver.`,
};

export const POS_INFORMATION_DEFAULTS: Record<string, NotePair> = {
  "printers:printersPrinters": VIRTUAL_PRINTER_LIST_NOTE,
  "settings:tab-0:workstation-setup": WORKSTATION_REGISTRATION_NOTE,
};

export function notePair(value: unknown, key: string): NotePair {
  const defaults = POS_INFORMATION_DEFAULTS[key] || { en: "", fr: "" };
  let en = "";
  let fr = "";
  if (typeof value === "string") en = value;
  else if (value && typeof value === "object") {
    const record = value as { en?: string; fr?: string };
    en = record.en || "";
    fr = record.fr || "";
  }
  if (!String(en).trim()) en = defaults.en || "";
  if (!String(fr).trim()) fr = defaults.fr || "";
  return { en, fr };
}

const COLORS: Record<string, string> = {
  red: "#b91c1c",
  green: "#15803d",
  blue: "#1d4ed8",
  orange: "#c2410c",
  navy: "#023047",
};

const TAG_PATTERN = /\[(h|b|big|red|green|blue|orange|navy|img)\]([\s\S]*?)\[\/\1\]/;

function imageAddress(value: string) {
  const url = String(value || "").trim();
  return /^https:\/\/\S+$/i.test(url) ? url : "";
}

function withBreaks(text: string, keyRef: { n: number }) {
  const parts = String(text).split("\n");
  const nodes: React.ReactNode[] = [];
  parts.forEach((part, index) => {
    if (index > 0) nodes.push(<br key={keyRef.n++} />);
    if (part) nodes.push(part);
  });
  return nodes;
}

function parseInline(input: string, keyRef: { n: number }): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let rest = input;
  while (rest) {
    const match = TAG_PATTERN.exec(rest);
    if (!match) {
      nodes.push(...withBreaks(rest, keyRef));
      break;
    }
    if (match.index > 0) nodes.push(...withBreaks(rest.slice(0, match.index), keyRef));
    const tag = match[1];
    const key = keyRef.n++;
    if (tag === "img") {
      const src = imageAddress(match[2]);
      if (src) nodes.push(<img key={key} src={src} alt="" style={{ display: "block", width: "100%", height: "auto", margin: "0.35em 0" }} />);
      rest = rest.slice(match.index + match[0].length);
      continue;
    }
    const inner = parseInline(match[2], keyRef);
    if (tag === "h") nodes.push(<span key={key} style={{ display: "block", fontSize: "1.2em", fontWeight: 700, margin: "0 0 0.35em" }}>{inner}</span>);
    else if (tag === "b") nodes.push(<strong key={key}>{inner}</strong>);
    else if (tag === "big") nodes.push(<span key={key} style={{ fontSize: "1.25em" }}>{inner}</span>);
    else nodes.push(<span key={key} style={{ color: COLORS[tag] }}>{inner}</span>);
    rest = rest.slice(match.index + match[0].length);
  }
  return nodes;
}

export function formatPosInformation(text: string) {
  const source = String(text || "").replace(/\r\n/g, "\n").trim();
  if (!source) return null;
  const keyRef = { n: 0 };
  return source.split(/\n\s*\n/).filter((paragraph) => paragraph.trim()).map((paragraph) => (
    <p key={keyRef.n++} style={{ margin: "0 0 0.75em" }}>
      {parseInline(paragraph.trim(), keyRef)}
    </p>
  ));
}

export function readPosInformationNotes(data: Record<string, unknown> | null | undefined) {
  const source = data && data.notes && typeof data.notes === "object" ? data.notes as Record<string, unknown> : {};
  const notes: Record<string, unknown> = { ...source };
  if (!data) return notes;
  Object.keys(data).forEach((key) => {
    if (key.indexOf("notes.") === 0) notes[key.slice(6)] = data[key];
  });
  return notes;
}

export function usePosInformationNotes() {
  const [notes, setNotes] = useState<Record<string, unknown>>({});
  useEffect(() => {
    let unsubscribeDoc = () => {};
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      unsubscribeDoc();
      if (!user) {
        setNotes({});
        return;
      }
      unsubscribeDoc = onSnapshot(
        doc(db, "systemConfig", "posInformation"),
        (snap) => {
          setNotes(readPosInformationNotes(snap.exists() ? snap.data() as Record<string, unknown> : null));
        }
      );
    });
    return () => {
      unsubscribeAuth();
      unsubscribeDoc();
    };
  }, []);
  return notes;
}
